/**
 * @description Base ApplePayButton implementation
 * @param {typeof import('widgets/Widget').default} Widget Base widget for extending
 * @returns {typeof ApplePayButton} Product Detail widget
 */
export default function (Widget) {
    /**
     * @class ApplePayButton
     * @augments Widget
     * @classdesc Represents ApplePayButton component with next features:
     * 1. Validate product before payment sheet is shown
     * 2. Render Apple Pay button
     * 3. Handle click on the Apple Pay button
     * @property {boolean} [data-ready-to-order=false] - ready to order flag
     * @property {string} data-text-select-options - select option text
     * @property {string} data-text-selected-qty-more-than-one - selected product quantity more then one text
     * @property {string} data-product - product model
     * @property {string} data-is-out-of-stock - indicates if product out of stock
     * @category widgets
     * @subcategory product
     * @example
     * cartridges/app_storefront_widgets/cartridge/templates/default/product/ApplePayButtons.isml
     *
     * <div
     *     data-widget="applePayButton"
     *     data-id="applePayButton"
     *     data-product="${JSON.stringify(product)}"
     *     data-is-out-of-stock="${isOutOfStock}"
     *     data-text-select-options="${Resource.msg('error.alert.select.options', 'product', null)}"
     *     data-text-selected-qty-more-than-one="${Resource.msg('applepay.qty.more.then.one', 'applePay', null)}"
     * >
     *     <div data-ref="container"></div>
     *     <script type="template/mustache" data-ref="template">
     *          <div
     *              data-ref="container"
     *          >
     *              {{${'#'}isApplePayAvailable}}
     *                  {{^isProductOutOfStock}}
     *                      <div class="b-apple_pay" data-ref="applePayContainer">
     *                          <isinclude template="/components/modules" sf-toolkit="off" />
     *                          <isapplepay
     *                              class="b-button m-width_full b-apple_pay-button js-apple-pay-button"
     *                              sku="{{product.id}}"
     *                          ></isapplepay>
     *                      </div>
     *                  {{/isProductOutOfStock}}
     *              {{/isApplePayAvailable}}
     *          </div>
     *      </script>
     * </div>
     */
    class ApplePayButton extends Widget {
        prefs() {
            return {
                availableToSellQty: 1,
                textSelectOptions: '',
                textSelectedQtyMoreThanOne: '',
                isOutOfStock: false,
                applePayButtonClass: 'js-apple-pay-button',
                ...super.prefs()
            };
        }

        /**
         * @description Widget initialization
         * @listens dom#click
         * @returns {void}
         */
        init() {
            super.init();

            if (!this.isApplePaySession()) {
                return;
            }

            this.productModel = this.prefs().productModel;
            this.renderApplePayButton(this.productModel)
                .then(() => {
                    this.attachEventHandler(this.prefs().applePayButtonClass, () => {
                        return this.validate(this.productModel);
                    });
                })
                // eslint-disable-next-line no-console
                .catch(() => { console.log('Product model not provided to the render method'); });
            this.onDestroy(() => this.cleanUpEvents());
        }

        /**
         * @description Product validation before payment sheet is shown
         * @emits "applepay.button.click.validation.error"
         * @param {object} product Product model
         * @returns {boolean} Result
         */
        validate(product) {
            let errorText = '';
            let isValid = true;

            if (!product.readyToOrder) {
                errorText = this.prefs().textSelectOptions;
                isValid = false;
            }

            if (this.prefs().availableToSellQty < product.selectedQuantity) {
                errorText = this.prefs().textSelectedQtyMoreThanOne;
                isValid = false;
            }

            if (!isValid) {
                /**
                 * @description Global event to Apple Pay validation error
                 * @event "applepay.button.click.validation.error"
                 */
                this.eventBus().emit('applepay.button.click.validation.error', errorText);
            }

            return isValid;
        }

        /**
        * @description Check if ApplePay enabled on site and Device supports it (same as SFCC script does).
        * @returns {boolean} Result
        */
        // eslint-disable-next-line class-methods-use-this
        isApplePaySession() {
            return window.dw?.applepay && window.ApplePaySession?.canMakePayments();
        }

        /**
         * @description Render Apple Pay button
         * @param {object} product Product model
         * @returns {Promise} Rendering
         */
        renderApplePayButton(product) {
            if (!product) { return Promise.reject(); }

            const isApplePayAvailable = this.isApplePaySession()
                && !product.availability?.isOutOfStock
                && !product.isProductWithOptions;

            this.productModel = product;

            return this.render(undefined, {
                isApplePayAvailable: isApplePayAvailable,
                product: product
            }, this.ref('container'));
        }

        /**
         * @description Start observing changes to the DOM that match the given options
         * @param {string} className Apple Pay button class name
         * @param {Function} handler Callback function to handle click
         * @returns {void}
         */
        attachEventHandler(className, handler) {
            if (this.mutationObserver) {
                this.cleanUpEvents();
            }

            this.findAndAttachHandler(className, handler);
            this.reattachOnDomModification(className, handler);
        }

        /**
        * @description Finds not processed and processes AP buttons of passed target element
         * @param {string} className Apple Pay button class name
         * @param {Function} handler Callback function to handle click
        * @returns {void}
        */
        findAndAttachHandler(className, handler) {
            const buttons = this.ref('self').get()?.getElementsByClassName(className) || [];

            Array.from(buttons).forEach((button) => {
                this.attachOnClickHandler(button, handler);
            });
        }

        /**
         * @description Attach callback function on DOM modification
         * @param {string} className Apple Pay button class name
         * @param {Function} handler Callback function to handle click
         * @returns {void}
         */
        reattachOnDomModification(className, handler) {
            if (this.mutationObserver) {
                return;
            }

            this.mutationObserver = new MutationObserver(() => {
                this.findAndAttachHandler(className, handler);
            });
            this.mutationObserver.observe(this.ref('self').get(), {
                childList: true,
                // eslint-disable-next-line spellcheck/spell-checker
                subtree: true,
                attributes: false
            });
        }

        /**
        * @description Override onclick handler added by DW script by ours, which will call DW one depending on passed handler's execution result.
        * @param {Element} applePayButton Button to add custom handler to
        * @param {object} handler Hash of 2 functions: 'click' handler, must return either true, to pass execution further, or return a Promise which will resolve with true value for the same;
        *     'attached' - optional, called at the moment when 'click' handler attached to button, button passed as an argument.
        */
        // eslint-disable-next-line class-methods-use-this
        attachOnClickHandler(applePayButton, handler) {
            if (applePayButton.originalApplePayOnClick) {
                return;
            }

            applePayButton.originalApplePayOnClick = applePayButton.onclick;

            applePayButton.onclick = function (event) {
                const result = handler.call(applePayButton, event);

                // execute built-in handler immediately if true returned
                if (result === true) {
                    applePayButton.originalApplePayOnClick.call(applePayButton, event);
                } else if (result && ('then' in result)) {
                    // execute built-in handler when returned Promise resolved with true
                    result.then(function (res) {
                        if (res === true) {
                            applePayButton.originalApplePayOnClick.call(applePayButton, event);
                        }
                    });
                }

                return false;
            };
        }

        /**
         * @description Remove event listener form the button
         * @returns {void}
         */
        cleanUpEvents() {
            const applePayButtons = this.ref('self').get()?.getElementsByClassName(this.prefs().applePayButtonClass) || [];

            Array.from(applePayButtons).forEach((button) => {
                if (button.originalApplePayOnClick) {
                    button.onclick = button.originalApplePayOnClick;
                    button.originalApplePayOnClick = undefined;
                }
            });

            if (this.mutationObserver) {
                this.mutationObserver.disconnect();
                this.mutationObserver = null;
            }
        }
    }

    return ApplePayButton;
}
